import { useState, useEffect } from 'react'
import { validateInvite, registerWithInvite, registerWithInviteGoogle } from '../utils/api'
import { setAuth } from '../utils/auth'
import GoogleSignInButton from './GoogleSignInButton'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  border:    'rgba(180,185,230,0.09)',
  accent:    '#b8bdd4',
  accentDim: 'rgba(180,185,230,0.12)',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  green:     '#4dc88a',
  red:       '#d45a5a',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

export default function RegisterPage({ token, onRegistered }) {
  const [invite, setInvite]           = useState(null)
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [authTab, setAuthTab]         = useState('google')   // 'google' | 'password'
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword]       = useState('')
  const [password2, setPassword2]     = useState('')
  const [submitting, setSubmitting]   = useState(false)
  const [formError, setFormError]     = useState(null)

  // linked_participant from invite metadata
  const linkedName = invite?.metadata?.linked_display_name || null
  const isLinked   = !!invite?.metadata?.linked_participant_id

  useEffect(() => {
    validateInvite(token)
      .then(data => {
        if (data.valid || data.ok) setInvite(data)
        else setError(data.error || 'Ссылка недействительна или уже использована')
      })
      .catch(() => setError('Ошибка проверки ссылки'))
      .finally(() => setLoading(false))
  }, [token])

  async function handlePasswordSubmit(e) {
    e.preventDefault()
    setFormError(null)
    const name = isLinked ? linkedName : displayName.trim()
    if (!name) return setFormError('Введи своё имя')
    if (password.length < 6) return setFormError('Пароль — минимум 6 символов')
    if (password !== password2) return setFormError('Пароли не совпадают')

    setSubmitting(true)
    try {
      const data = await registerWithInvite(token, name, password)
      if (data.ok && data.access_token) {
        setAuth(data); onRegistered?.(data)
      } else {
        setFormError(data.error || 'Ошибка регистрации')
      }
    } catch {
      setFormError('Ошибка соединения с сервером')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogle(idToken) {
    setFormError(null); setSubmitting(true)
    try {
      const data = await registerWithInviteGoogle(token, idToken)
      if (data.ok && data.access_token) {
        setAuth(data); onRegistered?.(data)
      } else {
        setFormError(data.error || 'Ошибка регистрации через Google')
      }
    } catch {
      setFormError('Ошибка соединения с сервером')
    } finally {
      setSubmitting(false)
    }
  }

  const box = {
    minHeight: '100vh', background: C.bg,
    backgroundImage: 'url(/stars.svg)',
    backgroundSize: 'cover', backgroundPosition: 'center',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  }
  const card = {
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${C.border}`,
    borderRadius: 20, padding: '40px 40px 36px',
    width: '100%', maxWidth: 400,
    backdropFilter: 'blur(12px)',
  }
  const inp = (err) => ({
    width: '100%', boxSizing: 'border-box',
    background: C.bg, border: `1px solid ${err ? C.red : C.border}`,
    borderRadius: 8, padding: '10px 14px',
    color: C.text, fontFamily: font.body, fontSize: 14,
    outline: 'none', marginBottom: 12,
  })

  if (loading) return (
    <div style={box}><div style={{ color: C.textMuted, fontFamily: font.body }}>Проверяю ссылку…</div></div>
  )

  if (error) return (
    <div style={box}>
      <div style={{ ...card, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontFamily: font.heading, fontSize: 24, color: C.accent, margin: '0 0 12px' }}>Ссылка недействительна</h2>
        <p style={{ fontFamily: font.body, fontSize: 14, color: C.textMuted, margin: '0 0 24px' }}>{error}</p>
        <a href="/" style={{ color: C.accent, fontFamily: font.body, fontSize: 13 }}>← На главную</a>
      </div>
    </div>
  )

  return (
    <div style={box}>
      <div style={card}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, letterSpacing: '0.15em', marginBottom: 8 }}>
            ◈ DIGITAL TWIN
          </div>
          <h1 style={{ fontFamily: font.heading, fontSize: 28, color: C.accent, margin: 0 }}>
            {isLinked ? 'Привязать аккаунт' : 'Создать аккаунт'}
          </h1>
          {isLinked && linkedName && (
            <div style={{
              marginTop: 12, padding: '8px 14px',
              background: 'rgba(180,185,230,0.06)', border: `1px solid ${C.border}`,
              borderRadius: 8, fontFamily: font.body, fontSize: 13, color: C.textMuted,
            }}>
              Профиль для: <strong style={{ color: C.accent }}>{linkedName}</strong>
            </div>
          )}
          {!isLinked && (
            <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, margin: '8px 0 0', lineHeight: 1.5 }}>
              Тебя пригласили. Выбери способ входа.
            </p>
          )}
        </div>

        {/* Google / Пароль вкладки */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {[
            { id: 'google',   label: 'Войти через Google' },
            { id: 'password', label: 'Email и пароль' },
          ].map(t => (
            <button key={t.id} onClick={() => { setAuthTab(t.id); setFormError(null) }}
              style={{
                flex: 1, padding: '7px 0', borderRadius: 7, cursor: 'pointer',
                fontFamily: font.body, fontSize: 12,
                background: authTab === t.id ? C.accentDim : 'transparent',
                border: `1px solid ${authTab === t.id ? C.border : C.border}`,
                color: authTab === t.id ? C.accent : C.textMuted,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Google */}
        {authTab === 'google' && (
          <div>
            <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, textAlign: 'center', marginBottom: 16, lineHeight: 1.5 }}>
              {isLinked
                ? 'Привяжи свой Google-аккаунт — он будет использоваться для входа.'
                : 'После входа через Google аккаунт будет создан автоматически.'
              }
            </p>
            <GoogleSignInButton onCredential={handleGoogle} disabled={submitting} />
            {submitting && (
              <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, textAlign: 'center', marginTop: 12 }}>
                Создаю аккаунт…
              </p>
            )}
          </div>
        )}

        {/* Email + пароль */}
        {authTab === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            {!isLinked && (
              <>
                <label style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Твоё имя</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="Как тебя зовут?"
                  autoFocus
                  style={inp(false)}
                />
              </>
            )}

            <label style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Пароль</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Минимум 6 символов"
              autoFocus={isLinked}
              style={inp(formError && password !== password2)}
            />

            <label style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Повторите пароль</label>
            <input
              type="password"
              value={password2}
              onChange={e => setPassword2(e.target.value)}
              placeholder="Ещё раз"
              style={inp(formError && password !== password2)}
            />

            <button
              type="submit"
              disabled={submitting || (!isLinked && !displayName) || !password || !password2}
              style={{
                width: '100%', padding: '11px 0', borderRadius: 8,
                cursor: 'pointer', fontFamily: font.body, fontSize: 14, fontWeight: 500,
                background: C.accentDim, border: `1px solid ${C.accent}`,
                color: C.accent,
                opacity: submitting || (!isLinked && !displayName) || !password || !password2 ? 0.5 : 1,
              }}
            >
              {submitting ? 'Создаю аккаунт…' : 'Зарегистрироваться'}
            </button>
          </form>
        )}

        {formError && (
          <p style={{ fontFamily: font.body, fontSize: 12, color: C.red, margin: '12px 0 0', textAlign: 'center' }}>
            {formError}
          </p>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <a href="/" style={{ color: C.textMuted, fontFamily: font.body, fontSize: 12 }}>← ai-digital-twin.ru</a>
        </div>
      </div>
    </div>
  )
}
